import { CommonModule } from '@angular/common';
import { Component, Inject, Optional, signal, Signal, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatMenuModule } from '@angular/material/menu';
import { notOnlyWhitespace } from '@app/_directives/form-validator.directive';
import { AlertService } from '@app/_services/alert.service';
import { SettingsService } from '@app/_services/settings.service';
import { InputTextEditorComponent } from '@app/components/shared/inputs/input-text-editor/input-text-editor.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { ConsolidatedApiService } from '@app/_services/consolidated-api.service';
import { CacheService } from '@app/_services/cache.service';
import { DataService } from '@app/_services/data.service';
import { PolicyType } from '@app/_types/generic.type';
import { PolicyDialogComponent } from '@app/components/shared/dialogs/policy-dialog/policy-dialog.component';
import { AuthService } from '@app/_services/auth.service';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '@app/_services/analytics.service';
import { log } from '@app/_services/logger';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { ModalService } from '@app/_services/modal.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
  selector: 'app-account-delete',
  imports: [
    CommonModule,
    MatMenuModule,
    SvgComponent,
    InputTextEditorComponent
  ],
  templateUrl: './account-delete.component.html',
  styleUrl: './account-delete.component.scss'
})

export class AccountDeleteComponent implements OnInit, OnDestroy {

  deleteForm!: FormGroup;
  deleteReasons: Signal<{ id: string, reason: string }[]> = this.dataService.accountDeleteReasons;
  unSubscribe = new Subject<void>();
  screenEnterTime: Signal<Date> = signal(new Date());
  public data: {};

  constructor(
    private fb: FormBuilder,
    public ss: SettingsService,
    private alertService: AlertService,
    @Optional() public dialogRef: MatDialogRef<AccountDeleteComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<AccountDeleteComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: {} | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: {} | null,
    private consolidatedApi: ConsolidatedApiService,
    public cc: CacheService,
    private dataService: DataService,
    private dialog: MatDialog,
    private router: Router,
    private as: AnalyticsService,
    private storageService: StorageService,
    private modalService: ModalService
  ) {
    // Use data from either dialog or bottom sheet
    this.data = (dialogData || bottomSheetData)!;

    this.deleteForm = this.fb.group({
      reason: ['', Validators.required],
      feedback: ['', [Validators.required, Validators.maxLength(20000), notOnlyWhitespace()]],
    });
  }

  ngOnInit() {
    this.analyticsInit();
  }

  get getReason(): { id: string, reason: string } | undefined {
    return this.deleteReasons().find(reason => reason.id === this.getFc('reason').value);
  }

  getFc(name: string): FormControl {
    return this.deleteForm.get(name) as FormControl;
  }

  selectReason(id: string) {
    this.getFc('reason').setValue(id);
  }

  async closeDialog() {
    this.close();
  }

  /**
   * Close dialog or bottom sheet (works with both)
   */
  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

  openPolicy(policyType: PolicyType) {

    const data = {
      type: policyType,
    };

    if (this.cc.isNarrowScreen()) {
      const setupDialog = this.modalService.openMainContent(
        PolicyDialogComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    } else {
      const setupDialog = this.modalService.openLevel1(
        PolicyDialogComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    }
  }

  async deleteConfirm() {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('common_deleteAccountTitle', { brandName: BRAND_CONFIG.name }),
      description: this.ss.t('deleteAccount_content'),
      yesButtonText: this.ss.t('common_delete'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    this.deleteAccount();
  }

  async deleteAccount() {
    this.cc.isLoading.set(true);
    const payload = {
      ...this.deleteForm.value,
      uid: this.cc.user.uid,
      userName: this.cc.user.name,
    };
    try {
      const response = await this.consolidatedApi.call('deletePermanently', payload);
      if (response.data && (response.data as { result?: string }).result === 'success') {
        this.storageService.clearSensitiveData();
        this.ss.reset();
        this.ss.viewSignals()['appTheme'].set('dark');
        this.ss.viewSignals()['themeColor'].set('a');
        this.router.navigate(['/account-deleted']);
      } else if (response.error) {
        log.error('deletePermanently', 'Error', { error: response.error });
      }
      this.closeDialog();
    } catch (error) {
      log.error('deletePermanently', 'Error', error as Error);
    } finally {
      this.cc.isLoading.set(false);
    }
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('deleteAccount', previousScreen);
        this.storageService.setCurrentScreen('deleteAccount');
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('deleteAccount', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }

}
